import { Icon, IconName } from "@zapier/design-system";
import { APIField } from "server/services/tables/types/fields";
import { APIRecord } from "server/services/tables/types/records";
import { isBoolean, isString } from "lodash";
import React, { ReactNode, useRef, useState } from "react";
import { Order } from "server/schemas/table-records";
import { MenuButton, MenuButtonProps } from "./MenuButton";
import { ScrollWrapper } from "blocks/components/ScrollWrapper";
import { InteractButtonProps } from "blocks/components/InteractButton";
import { FieldIcon } from "./FieldIcon";
import { Props as BooleanFieldDisplayProps } from "./BooleanFieldDisplay";
import { prettyValue } from "./utils";
import { cn } from "utils/cn";
import dynamic from "next/dynamic";

const InteractButton = dynamic(
  () =>
    import("../../../components/InteractButton").then(
      (mod) => mod.InteractButton
    ),
  {
    ssr: false,
    loading() {
      return null;
    },
  }
);

const BooleanFieldDisplay = dynamic(
  () => import("./BooleanFieldDisplay").then((mod) => mod.BooleanFieldDisplay),
  {
    ssr: false,
    loading() {
      return null;
    },
  }
);

const HtmlTable: React.FunctionComponent<
  React.ComponentPropsWithRef<"table">
> = (props) => (
  <table {...props} className={cn("custom-table", props.className)} />
);

type OrderDirection = "asc" | "desc" | undefined;

export function TableView({
  RenderBooleanFieldDisplay = BooleanFieldDisplay,
  RenderInteractButton = InteractButton,
  ...props
}: {
  accountId?: number;
  tableId: string;
  fields: APIField[];
  records: APIRecord[];
  order: Order | undefined;
  onOrderChange: (order: Order | undefined) => void;
  menuItems: MenuButtonProps["menuItems"];
  disableScroll?: boolean;
  isDisabled?: boolean;
  canUpdateRecord?: boolean;
  tableBlockId?: string;
  RenderBooleanFieldDisplay?: React.ComponentType<BooleanFieldDisplayProps>;
  RenderInteractButton?: React.ComponentType<
    InteractButtonProps & React.RefAttributes<HTMLButtonElement>
  >;
}) {
  const tableRef = useRef<HTMLTableElement>(null);

  // These are the field types the Tables API allows to be used as ordering fields
  const allowedOrderingFieldTypes = [
    "multiple_string",
    "currency",
    "decimal",
    "boolean",
    "multiple_uuid",
    "email",
    "string",
    "multiple_decimal",
    "uuid",
    "multiple_number",
    "number",
    "phone_number",
    "multiple_email",
    "datetime",
    "multiple_text",
    "text",
  ];

  if (!props.fields.length) return <div>Empty table</div>;

  const renderColumnHeader = () => {
    const cells = props.fields.map((field: APIField): ReactNode => {
      const fieldKey = `f${field.id}`;

      const isAllowedOrderingField = allowedOrderingFieldTypes.includes(
        field.type
      );

      let orderDirection: OrderDirection = undefined;

      if (props.order?.key === fieldKey) orderDirection = props.order.direction;

      const handleClick = () => {
        let nextDirection: OrderDirection;
        if (orderDirection === undefined) nextDirection = "asc";
        else if (orderDirection === "asc") nextDirection = "desc";
        else nextDirection = undefined;

        if (nextDirection)
          props.onOrderChange({ key: fieldKey, direction: nextDirection });
        else props.onOrderChange(undefined);
      };

      return (
        <th key={field.id}>
          <button
            className="grid w-full cursor-pointer items-center text-start [grid-template-columns:1fr_auto]"
            onClick={handleClick}
            disabled={!isAllowedOrderingField || props.isDisabled}
          >
            <span className="flex w-full items-center gap-1">
              {field.options?.icon && (
                <FieldIcon iconSlug={field.options.icon} />
              )}
              {field.name}
            </span>
            <div className="text-zi-gray">
              <Icon
                name={getSortIconName(orderDirection)}
                size={20}
                color="currentColor"
                isBlock
              />
            </div>
          </button>
        </th>
      );
    });

    if (props.menuItems.length) cells.push(<th key="menu" data-menu="true" />);

    return cells;
  };

  interface ZapButton {
    custom_label: string | undefined;
    is_enabled?: boolean;
  }

  return (
    <ScrollWrapper disableScroll={props.disableScroll}>
      <HtmlTable ref={tableRef}>
        <thead>
          <tr>{renderColumnHeader()}</tr>
        </thead>
        <tbody>
          {props.records?.map((record) => {
            const cells = props.fields.map((field) => {
              if (
                field.type === "button_trigger_zap" ||
                field.type === "button_continue_zap"
              ) {
                const zapButton: ZapButton = record.data[
                  `f${field.id}`
                ] as ZapButton;
                const customLabel = zapButton?.custom_label;
                // Tables allow fields to have a default behavior that can be overridden on the record level
                // so we first check for a setting on the record, and if there isn't one we default to
                // the field setting
                const isRecordButtonEnabled = isBoolean(zapButton?.is_enabled)
                  ? zapButton?.is_enabled
                  : field.options?.button?.is_enabled;
                const isButtonEnabled =
                  !field.options?.locked && isRecordButtonEnabled;

                return (
                  <TableCell key={field.id}>
                    <RenderInteractButton
                      tableId={props.tableId}
                      fieldId={field.id}
                      recordId={record.id}
                      isEnabled={isButtonEnabled}
                      isDisabled={props.isDisabled}
                    >
                      {customLabel ?? field.options?.button?.label}
                    </RenderInteractButton>
                  </TableCell>
                );
              }

              if (field.type === "boolean" && props.canUpdateRecord) {
                return (
                  <TableCell key={field.id}>
                    <RenderBooleanFieldDisplay
                      tableId={props.tableId}
                      recordId={record.id}
                      field={field}
                      blockId={props.tableBlockId}
                      value={record.data["f" + field.id] as boolean}
                    />
                  </TableCell>
                );
              }

              return (
                <TableCell key={field.id}>
                  {prettyValue(field, record.data["f" + field.id])}
                </TableCell>
              );
            });

            if (props.menuItems.length) {
              cells.push(
                <td key="menu" data-menu="true">
                  <MenuButton
                    menuItems={props.menuItems.map((item) => ({
                      title: item.title,
                      onClick: item.onClick.bind(null, record.id),
                      color: item.color,
                    }))}
                    placement="bottom-end"
                    isDisabled={props.isDisabled}
                  />
                </td>
              );
            }

            return <tr key={record.id}>{cells}</tr>;
          })}
        </tbody>
      </HtmlTable>
    </ScrollWrapper>
  );
}

function TableCell(props: { children: ReactNode }) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (isString(props.children)) {
    if (props.children.length > 130) {
      const content = isExpanded
        ? props.children
        : `${props.children.slice(0, 100)}...`;

      return (
        <td style={{ minWidth: "400px" }}>
          {content} &nbsp;&nbsp;
          <button
            type="button"
            className="cursor-pointer text-zi-primary underline"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? "less" : "more"}
          </button>
        </td>
      );
    }
    if (props.children.length > 50) {
      return (
        <td style={{ minWidth: "300px" }}>{props.children.slice(0, 50)}</td>
      );
    }
  }

  return <td>{props.children}</td>;
}

function getSortIconName(direction: OrderDirection): IconName {
  switch (direction) {
    case "asc":
      return "arrowSmallDown";
    case "desc":
      return "arrowSmallUp";
    case undefined:
      return "arrowDropdown";
    default:
      const _exhaustiveCheck: never = direction;
      return _exhaustiveCheck;
  }
}
